import { FileSpreadsheet, Printer, ChevronLeft, Search, CheckSquare, Square, Loader2, Award, RefreshCcw, Trash2, Eye, Monitor } from 'lucide-react';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { PracticeExam } from '../../types/practice-exam';
import { ExamResultList } from './components/ExamResultList';
import WebStudentReportCard from './components/WebStudentReportCard';
import { useAuth } from '../../contexts/AuthContext';
import { ApiService } from '../../services/api';
import { useModal } from '../../hooks/useModal';

function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
}

export default function ReportCards() {
    const { showAlert, showConfirm } = useModal();
    const { systemConfig } = useAuth();
    const [loading, setLoading] = useState(false);
    const [exams, setExams] = useState<PracticeExam[]>([]);
    const [selectedExam, setSelectedExam] = useState<PracticeExam | null>(null);

    const [resultsLoading, setResultsLoading] = useState(false);
    const [bulkActionLoading, setBulkActionLoading] = useState<'delete' | 'regenerate' | null>(null);
    const [resultsData, setResultsData] = useState<any[]>([]);
    const [examMetadata, setExamMetadata] = useState<{ bookletMapping?: any; testNames?: any; testAverages?: any; overallStats?: any; levelType?: PracticeExam['levelType']; yksMode?: PracticeExam['yksMode'] }>({});

    const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const debouncedSearch = useDebounce(searchQuery, 300);

    const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
    const [previewMode, setPreviewMode] = useState<'system' | 'a4'>('system');

    const printRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchExams();
    }, []);

    const fetchExams = async () => {
        try {
            setLoading(true);

            const res = await ApiService.getPracticeExams({ limit: 100 });

            const allExams = Array.isArray((res as any)?.rows) ? (res as any).rows : Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

            console.log('Fetched Exams:', allExams);

            const examsWithResults = allExams.filter((e: any) => e.stats === true);
            setExams(examsWithResults);
        } catch (error) {
            console.error(error);
            showAlert({ title: 'Hata', message: 'Denemeler yüklenirken hata oluştu', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const loadExamResults = async (exam: PracticeExam) => {
        const res = await ApiService.getPracticeExamResults(exam.id, { limit: 10000 });
        const rows = Array.isArray((res as any)?.rows) ? (res as any).rows : [];
        setResultsData(rows);
        setExamMetadata({
            bookletMapping: (res as any)?.bookletMapping || {},
            testNames: (res as any)?.testNames || {},
            testAverages: (res as any)?.testAverages || {},
            overallStats: (res as any)?.overallStats || {},
            levelType: exam.levelType,
            yksMode: exam.yksMode,
        });
    };

    const handleExamClick = async (exam: PracticeExam) => {
        try {
            setSelectedExam(exam);
            setResultsLoading(true);
            setViewMode('detail');
            setSelectedStudentIds(new Set());
            setSearchQuery("");
            await loadExamResults(exam);
        } catch (error: any) {
            showAlert({ title: 'Hata', message: error.message || 'Sonuçlar yüklenirken hata oluştu', type: 'error' });
            setViewMode('list');
            setSelectedExam(null);
        } finally {
            setResultsLoading(false);
        }
    };

    const handleBack = () => {
        setViewMode('list');
        setSelectedExam(null);
        setResultsData([]);
    };

    const filteredStudents = useMemo(() => {
        if (!debouncedSearch) return resultsData;
        const lower = debouncedSearch.toLowerCase();
        return resultsData.filter(row => {
            const studentInfo = row?.results?.studentInfo || {};
            const fullName = (row?.student?.firstName && row?.student?.lastName)
                ? `${row.student.firstName} ${row.student.lastName}`
                : (studentInfo.fullName || row?.student?.metadata?.parsedName || '');
            const tc = studentInfo.tc || row?.student?.metadata?.tcNumber || '';
            const schoolCode = studentInfo.schoolCode || '';
            return fullName.toLowerCase().includes(lower) ||
                tc.includes(lower) ||
                schoolCode.includes(lower);
        });
    }, [resultsData, debouncedSearch]);

    const toggleSelection = (id: string) => {
        const next = new Set(selectedStudentIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedStudentIds(next);
    };

    const toggleAll = () => {
        if (selectedStudentIds.size === filteredStudents.length) {
            setSelectedStudentIds(new Set());
        } else {
            setSelectedStudentIds(new Set(filteredStudents.map(s => s.id)));
        }
    };

    const handlePrint = () => {
        if (selectedStudentIds.size === 0) {
            showAlert({ title: 'Uyarı', message: 'Lütfen en az bir öğrenci seçin.', type: 'warning' });
            return;
        }

        window.print();
    };

    const handleBulkDelete = async () => {
        if (!selectedExam || selectedStudentIds.size === 0) {
            await showAlert({ title: 'Uyarı', message: 'Lütfen en az bir öğrenci seçin.', type: 'warning' });
            return;
        }

        const confirmed = await showConfirm({
            title: 'Seçili Sonuçları Sil',
            message: `${selectedStudentIds.size} öğrencinin bu denemedeki tüm cevapları ve sonucu silinecek. Bu işlem geri alınamaz.`,
            confirmText: 'Sil',
            cancelText: 'Vazgeç',
            confirmButtonVariant: 'danger',
        });

        if (!confirmed) return;

        try {
            setBulkActionLoading('delete');
            await ApiService.bulkDeletePracticeExamResults(selectedExam.id, Array.from(selectedStudentIds));
            setSelectedStudentIds(new Set());
            await loadExamResults(selectedExam);
            await showAlert({ title: 'Başarılı', message: 'Seçili öğrencilerin cevapları silindi.', type: 'success' });
        } catch (error: any) {
            await showAlert({ title: 'Hata', message: error.message || 'Seçili sonuçlar silinirken hata oluştu', type: 'error' });
        } finally {
            setBulkActionLoading(null);
        }
    };

    const handleBulkRegenerate = async () => {
        if (!selectedExam || selectedStudentIds.size === 0) {
            await showAlert({ title: 'Uyarı', message: 'Lütfen en az bir öğrenci seçin.', type: 'warning' });
            return;
        }

        const confirmed = await showConfirm({
            title: 'Seçili Sonuçları Tekrar Oluştur',
            message: `${selectedStudentIds.size} öğrencinin mevcut cevapları korunarak sonuçları yeniden hesaplanacak.`,
            confirmText: 'Tekrar Oluştur',
            cancelText: 'Vazgeç',
            confirmButtonVariant: 'primary',
        });

        if (!confirmed) return;

        try {
            setBulkActionLoading('regenerate');
            await ApiService.bulkRegeneratePracticeExamResults(selectedExam.id, Array.from(selectedStudentIds));
            await loadExamResults(selectedExam);
            await showAlert({ title: 'Başarılı', message: 'Seçili öğrencilerin sonuçları yeniden oluşturuldu.', type: 'success' });
        } catch (error: any) {
            await showAlert({ title: 'Hata', message: error.message || 'Seçili sonuçlar yeniden oluşturulurken hata oluştu', type: 'error' });
        } finally {
            setBulkActionLoading(null);
        }
    };

    if (viewMode === 'list') {
        return (
            <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">Karne Modülü</h2>
                        <p className="text-sm text-gray-500">Sonuçları yüklenmiş denemelerin karnelerini oluşturun ve indirin.</p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                ) : exams.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-12 text-center border-2 border-dashed border-gray-200">
                        <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900">Henüz Sonuçlanan Deneme Yok</h3>
                        <p className="text-gray-500 mt-1">Karne oluşturmak için önce "Tüm Denemeler" sekmesinden sonuç yükleyiniz.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {exams.map((exam) => (
                            <div
                                key={exam.id}
                                onClick={() => handleExamClick(exam)}
                                className="group bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer relative overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <Award className="w-16 h-16 text-blue-600 transform rotate-12" />
                                </div>

                                <h3 className="text-lg font-semibold text-gray-800  line-clamp-2" title={exam.name}>
                                    {exam.name}
                                </h3>
                                <p className="text-sm text-gray-500 mb-4 flex items-center gap-2">
                                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                                        {new Date(exam.date || '').toLocaleDateString('tr-TR')}
                                    </span>
                                    <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs font-medium">
                                        Sonuçlar Hazır
                                    </span>
                                </p>

                                <div className="flex items-center text-sm font-medium text-blue-600 group-hover:translate-x-1 transition-transform">
                                    Raporları Görüntüle
                                    <ChevronLeft className="w-4 rotate-180 ml-1" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-[calc(100vh-200px)] overflow-y-auto md:h-[calc(100vh-200px)] md:overflow-hidden">

            <div className="flex flex-col gap-3 p-4 border-b border-gray-200 bg-white sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-start gap-3 sm:items-center sm:gap-4">
                    <button
                        onClick={handleBack}
                        className="shrink-0 p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600"
                    >
                        <ChevronLeft size={24} />
                    </button>
                    <div className="min-w-0">
                        <h2 className="text-base font-bold leading-tight text-gray-800 break-words sm:text-lg">{selectedExam?.name}</h2>
                        <p className="text-xs text-gray-500">Raporlar ve Karne İşlemleri</p>
                    </div>
                </div>

                <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:justify-end">
                    <button
                        onClick={handleBulkRegenerate}
                        disabled={selectedStudentIds.size === 0 || bulkActionLoading !== null}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4 sm:py-2"
                    >
                        {bulkActionLoading === 'regenerate' ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />}
                        <span>Tekrar Oluştur</span>
                    </button>
                    <button
                        onClick={handleBulkDelete}
                        disabled={selectedStudentIds.size === 0 || bulkActionLoading !== null}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4 sm:py-2"
                    >
                        {bulkActionLoading === 'delete' ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        <span>Cevapları Sil</span>
                    </button>
                    <button
                        onClick={() => setPreviewMode(previewMode === 'system' ? 'a4' : 'system')}
                        disabled={selectedStudentIds.size === 0}
                        className={`flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4 sm:py-2 ${
                            previewMode === 'a4'
                                ? 'bg-purple-600 text-white hover:bg-purple-700'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                        title={previewMode === 'a4' ? 'Sistem görünümüne dön' : 'A4 çıktı önizlemesi'}
                    >
                        {previewMode === 'a4' ? <Monitor size={16} /> : <Eye size={16} />}
                        <span className="hidden sm:inline">{previewMode === 'a4' ? 'Sistem Görünümü' : 'A4 Önizleme'}</span>
                        <span className="sm:hidden">{previewMode === 'a4' ? 'Sistem' : 'A4'}</span>
                    </button>
                    <button
                        onClick={handlePrint}
                        disabled={selectedStudentIds.size === 0 || bulkActionLoading !== null}
                        className="col-span-2 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4 sm:py-2"
                    >
                        <Printer size={16} />
                        <span className="sm:hidden">{selectedStudentIds.size > 0 ? `${selectedStudentIds.size} Seçiliyi Yazdır` : 'Seçim Yapınız'}</span>
                        <span className="hidden sm:inline">{selectedStudentIds.size > 0 ? `${selectedStudentIds.size} Seçiliyi Yazdır / İndir` : 'Seçim Yapınız'}</span>
                    </button>
                </div>
            </div>

            <div className="flex flex-col overflow-visible md:flex-1 md:flex-row md:overflow-hidden">

                <div className="flex w-full flex-col bg-gray-50 border-b border-gray-200 md:w-1/3 md:border-b-0 md:border-r lg:w-1/4">
                    <div className="p-3 border-b border-gray-200 bg-white">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4" />
                            <input
                                type="text"
                                placeholder="Öğrenci ara..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            />
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2 px-1 text-xs text-gray-500">
                            <span>{filteredStudents.length} öğrenci bulundu</span>
                            <button onClick={toggleAll} className="shrink-0 text-blue-600 hover:underline">
                                {selectedStudentIds.size === filteredStudents.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
                            </button>
                        </div>
                    </div>

                    <div className="p-2 space-y-1 md:flex-1 md:overflow-y-auto">
                        {resultsLoading && (
                            <div className="p-4 text-center text-gray-500 text-sm">Yükleniyor...</div>
                        )}
                        {!resultsLoading && filteredStudents.map(student => {
                            const isSelected = selectedStudentIds.has(student.id);
                            const studentInfo = student?.results?.studentInfo || {};
                            const fullName = (student?.student?.firstName && student?.student?.lastName)
                                ? `${student.student.firstName} ${student.student.lastName}`
                                : (studentInfo.fullName || student?.student?.metadata?.parsedName || 'İsimsiz Öğrenci');

                            return (
                                <div
                                    key={student.id}
                                    onClick={() => toggleSelection(student.id)}
                                    className={`flex items-start gap-3 rounded-lg border p-3 transition-all sm:items-center cursor-pointer ${isSelected
                                        ? 'bg-blue-50 border-blue-200 shadow-sm'
                                        : 'bg-white border-transparent hover:bg-gray-100 hover:border-gray-200'
                                        }`}
                                >
                                    <div className={`flex-shrink-0 text-blue-600`}>
                                        {isSelected ? <CheckSquare size={18} /> : <Square size={18} className="text-gray-400" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}>
                                            {fullName}
                                        </p>
                                        <p className="text-xs text-gray-400 flex items-center gap-1">
                                            <span>{student?.student?.department?.name || 'Sınıf Yok'}</span>
                                            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                                            <span>{student?.student?.studentNumber || student?.schoolCode || studentInfo?.schoolCode || '-'}</span>
                                        </p>
                                    </div>
                                    <div className="shrink-0 text-right">
                                        <span className={`text-xs font-bold ${(student.stats?.scorePercentage || 0) >= 50 ? 'text-green-600' : 'text-red-500'
                                            }`}>
                                            %{student.stats?.scorePercentage?.toFixed(0)}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="bg-gray-100 p-3 md:hidden">
                    {selectedStudentIds.size > 0 ? (
                        previewMode === 'a4' ? (
                            <div className="overflow-x-auto overflow-y-visible pb-4">
                                <div className="flex flex-col items-center gap-6" style={{ minWidth: '210mm' }}>
                                    {Array.from(selectedStudentIds).map((id, idx) => {
                                        const studentData = resultsData.find(s => s.id === id);
                                        if (!studentData) return null;
                                        return (
                                            <div key={id} className="bg-white shadow-xl border border-gray-300 relative" style={{ width: '210mm', minHeight: '297mm', padding: '10mm', boxSizing: 'border-box' }}>
                                                <div className="absolute top-2 right-3 bg-purple-100 text-purple-700 text-[10px] font-medium px-2 py-0.5 rounded-full">
                                                    {idx + 1} / {selectedStudentIds.size}
                                                </div>
                                                <StudentReportCard data={getStudentReportData(studentData, examMetadata)} examName={selectedExam?.name} isPrint companyLogo={systemConfig?.companyLogo || undefined} companyName={systemConfig?.companyName || undefined} totalStudents={resultsData.length} />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className="overflow-x-auto overflow-y-visible rounded-2xl bg-white shadow-sm">
                                <div className="min-w-[980px]">
                                    {(() => {
                                        const firstId = Array.from(selectedStudentIds)[0];
                                        const studentData = resultsData.find(s => s.id === firstId);
                                        if (!studentData) return null;

                                        const data = getStudentReportData(studentData, examMetadata);
                                        return <WebStudentReportCard data={data} examName={selectedExam?.name} companyLogo={systemConfig?.companyLogo || undefined} companyName={systemConfig?.companyName || undefined} totalStudents={resultsData.length} />;
                                    })()}
                                </div>
                            </div>
                        )
                    ) : (
                        <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                            Önizleme görmek için bir öğrenci seçin.
                        </div>
                    )}
                </div>

                <div className="hidden md:flex flex-1 bg-gray-100 p-8 overflow-auto justify-center items-start">
                    {selectedStudentIds.size > 0 ? (
                        previewMode === 'a4' ? (
                            <div className="flex flex-col items-center gap-8 w-full">
                                {Array.from(selectedStudentIds).map((id, idx) => {
                                    const studentData = resultsData.find(s => s.id === id);
                                    if (!studentData) return null;
                                    return (
                                        <div key={id} className="bg-white shadow-2xl border border-gray-300 relative mx-auto" style={{ width: '210mm', minHeight: '297mm', padding: '10mm', boxSizing: 'border-box' }}>
                                            <div className="absolute top-2 right-3 bg-purple-100 text-purple-700 text-[10px] font-medium px-2 py-0.5 rounded-full z-10">
                                                {idx + 1} / {selectedStudentIds.size}
                                            </div>
                                            <StudentReportCard data={getStudentReportData(studentData, examMetadata)} examName={selectedExam?.name} isPrint companyLogo={systemConfig?.companyLogo || undefined} companyName={systemConfig?.companyName || undefined} totalStudents={resultsData.length} />
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div
                                className="bg-white shadow-2xl rounded-sm flex flex-col overflow-hidden"
                                style={{ width: '297mm', minHeight: '210mm', padding: '10mm' }}
                            >
                                {(() => {
                                    const firstId = Array.from(selectedStudentIds)[0];
                                    const studentData = resultsData.find(s => s.id === firstId);
                                    if (!studentData) return null;

                                    const data = getStudentReportData(studentData, examMetadata);
                                    return <WebStudentReportCard data={data} examName={selectedExam?.name} companyLogo={systemConfig?.companyLogo || undefined} companyName={systemConfig?.companyName || undefined} totalStudents={resultsData.length} />;
                                })()}
                            </div>
                        )
                    ) : (
                        <ExamResultList
                            data={filteredStudents}
                            metadata={examMetadata}
                            examName={selectedExam?.name}
                            examDate={selectedExam?.date}
                            companyLogo={systemConfig?.companyLogo || undefined}
                            companyName={systemConfig?.companyName || undefined}
                        />
                    )}
                </div>
            </div>

            <div className="hidden print:block print-area fixed inset-0 bg-white z-[99999] overflow-visible">
                <style>{`
            @media print {
               @page {
                 size: A4;
                 margin: 0;
               }

               html, body {
                 background: white !important;
                 -webkit-print-color-adjust: exact !important;
                 print-color-adjust: exact !important;
                 margin: 0 !important;
                 padding: 0 !important;
                 overflow: visible !important;
                 height: auto !important;
               }

               #root, #app, .App {
                 display: block !important;
                 margin: 0 !important;
                 padding: 0 !important;
                 width: 100% !important;
               }

               aside,
               [class*="sidebar"],
               [class*="Sidebar"],
               [class*="left-0"][class*="fixed"],
               [class*="bg-white"][class*="shadow-lg"][class*="flex-col"] {
                 display: none !important;
                 width: 0 !important;
                 min-width: 0 !important;
                 max-width: 0 !important;
                 visibility: hidden !important;
                 position: absolute !important;
                 left: -9999px !important;
               }

               header,
               nav,
               [class*="navbar"],
               [class*="Navbar"],
               [class*="h-14"][class*="bg-white"],
               [class*="h-16"][class*="bg-white"],
               [class*="border-b"][class*="fixed"][class*="top-0"] {
                 display: none !important;
                 height: 0 !important;
                 min-height: 0 !important;
                 max-height: 0 !important;
                 visibility: hidden !important;
                 position: absolute !important;
                 top: -9999px !important;
               }

               .print-page-break {
                 page-break-after: always;
                 break-after: page;
               }

               .no-print {
                 display: none !important;
                 visibility: hidden !important;
               }

               .print-area {
                 display: block !important;
                 visibility: visible !important;
                 position: absolute !important;
                 top: 0 !important;
                 left: 0 !important;
                 right: 0 !important;
                 z-index: 99999 !important;
                 background: white !important;
               }

               .print-page-break {
                 padding: 10mm !important;
                 box-sizing: border-box !important;
               }

               .print-area table {
                 border-collapse: collapse !important;
                 border-spacing: 0 !important;
               }

               .print-area th,
               .print-area td {
                 -webkit-print-color-adjust: exact !important;
                 print-color-adjust: exact !important;
               }

               .print-area .border-l {
                 border-left: 1px solid #000 !important;
               }

               .print-area .border-r {
                 border-right: 1px solid #000 !important;
               }

               .print-area .border-r-2 {
                 border-right: 2px solid #000 !important;
               }

               .print-area .border-r-black {
                 border-right-color: #000 !important;
               }

               .print-area .border-t {
                 border-top: 1px solid #000 !important;
               }

               .print-area .border-b {
                 border-bottom: 1px solid #000 !important;
               }

               .print-area .border-black {
                 border-color: #000 !important;
               }

               .print-area .border {
                 border: 1px solid #000 !important;
               }
            }
         `}</style>

                {Array.from(selectedStudentIds).map(id => {
                    const studentData = resultsData.find(s => s.id === id);
                    if (!studentData) return null;
                    return (
                        <div key={id} className="print-page-break w-[210mm] h-[297mm] relative mx-auto bg-white p-8 box-border">
                            <StudentReportCard data={getStudentReportData(studentData, examMetadata)} examName={selectedExam?.name} isPrint companyLogo={systemConfig?.companyLogo || undefined} companyName={systemConfig?.companyName || undefined} totalStudents={resultsData.length} />
                        </div>
                    )
                })}
            </div>
        </div>
    );
}

export const getStudentReportData = (studentRow: any, examMetadata: any) => {
    const stats = studentRow.stats || {};
    const answers = studentRow.answers || {};
    const booklets = examMetadata.bookletMapping || {};
    const testNames = examMetadata.testNames || {};
    const testAverages = examMetadata.testAverages || {};

    const subjects = Object.entries(answers).map(([testId, data]: [string, any]) => {
        const name = testNames[testId] || data.testName || `Test ${testId}`;
        const avgData = testAverages[testId] || {};

        return {
            name,
            testId,
            totalQ: data.totalQuestions || 0,
            correct: data.correct || 0,
            incorrect: data.incorrect || 0,
            empty: data.unanswered || 0,
            net: data.net || (data.correct - (data.incorrect * 0.25)),
            subjects: data.subjects || [],
            questions: data.questions || [],
            avgNet: avgData.avgNet || 0,
            avgSuccessRate: avgData.successRate || 0
        };
    });

    const allQuestions: any[] = [];
    Object.entries(answers).forEach(([testId, data]: [string, any]) => {
        const testName = testNames[testId] || data.testName || `Test ${testId}`;
        (data.questions || []).forEach((q: any) => {
            allQuestions.push({
                ...q,
                testId,
                testName
            });
        });
    });

    const achievements: any[] = [];
    allQuestions.forEach(q => {
        if (q.achievement) {
            const existing = achievements.find(a => a.name === q.achievement && a.subject === (q.subjectName || q.testName));
            if (existing) {
                existing.total++;
                if (q.isCorrect) existing.correct++;
                else if (q.isAnswered) existing.incorrect++;
            } else {
                achievements.push({
                    subject: q.subjectName || q.testName,
                    name: q.achievement,
                    total: 1,
                    correct: q.isCorrect ? 1 : 0,
                    incorrect: q.isAnswered && !q.isCorrect ? 1 : 0
                });
            }
        }
    });

    return {
        student: studentRow.student,
        info: studentRow.results?.studentInfo || {},
        stats,
        subjects,
        allQuestions,
        achievements,
        bookletType: studentRow.results?.studentInfo?.bookletType || 'A',
        schoolCode: studentRow.schoolCode,
        testAverages: testAverages,
        overallStats: examMetadata.overallStats || {},
        examConfig: {
            levelType: examMetadata.levelType || null,
            yksMode: examMetadata.yksMode || null,
        }
    };
};

export const StudentReportCard = ({ data, examName, isPrint, companyLogo, companyName, totalStudents = 0 }: { data: any, examName?: string, isPrint?: boolean, companyLogo?: string, companyName?: string, totalStudents?: number }) => {
    const { student, info, stats, subjects, bookletType, schoolCode, allQuestions = [], achievements = [], testAverages = {}, overallStats = {}, examConfig = {} } = data;

    const safeStr = (s: any) => s || '';
    const safeNum = (n: any) => Number(n) || 0;
    const fmt = (n: any) => typeof n === 'number' ? n.toFixed(2) : '0.00';

    const fullName = (student?.firstName && student?.lastName)
        ? `${student.firstName} ${student.lastName}`
        : (info.fullName || student?.metadata?.parsedName || 'Öğrenci');

    const studentNo = '*****';
    const sube = student?.department?.name || '11-B';
    const rawTc = student?.metadata?.tcNumber || '';
    const tcNo = rawTc && rawTc.length >= 6 ? rawTc.substring(0, 6) + '*****' : (rawTc || '***********');

    const examTitle = examName || "TÜRKİYE GENELİ TYT İLK PROVA (T1225)";

    const findSub = (name: string) => subjects.find((s: any) => s.name?.toLowerCase().includes(name.toLowerCase())) || { totalQ: 0, correct: 0, incorrect: 0, empty: 0, net: 0, questions: [] };

    const normalizeExamLabel = (value: string) => (value || '')
        .toLowerCase()
        .replace(/ı/g, 'i')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

    const normalizeBranchSubject = (value: string) => normalizeExamLabel(value || '');
    const branchSelections = info?.branchSelections || student?.metadata?.branchSelections || {};

    const filterAlternativeBranchQuestions = (questions: any[]) => {
        return questions.filter((question: any) => {
            const testId = Number(question?.testId || 0);
            const subject = normalizeBranchSubject(question?.subjectName || '');

            if (testId === 2) {
                const selected = branchSelections?.sosyal_bilimler === 'felsefe' ? 'felsefe' : 'din';
                if (selected === 'felsefe') return !subject.includes('din kulturu');
                return !subject.includes('ilave felsefe');
            }

            if (testId === 6) {
                const selected = branchSelections?.sosyal_bilimler2 === 'felsefe' ? 'felsefe' : 'din';
                if (selected === 'felsefe') return !subject.includes('din kulturu');
                return !subject.includes('ilave felsefe');
            }

            return true;
        });
    };

    const getColumnRank = (name: string, totalQ: number, originalIndex: number) => {
        const normalized = normalizeExamLabel(name);

        if (normalized === 'turkce') return 1;
        if (normalized === 'sosyal bilimler') return originalIndex >= 4 ? 6 : 2;
        if (normalized === 'temel matematik') return 3;
        if (normalized === 'fen bilimleri') return totalQ > 20 ? 8 : 4;
        if (normalized.includes('turk dili ve edebiyati') || normalized.includes('edebiyati sosyal bilimler 1')) return 5;
        if (normalized === 'sosyal bilimler 2') return 6;
        if (normalized === 'matematik') return 7;

        return 100 + originalIndex;
    };

    const getCompactTestLabel = (name: string) => {
        const normalized = normalizeExamLabel(name);

        if (normalized === 'turkce') return 'TÜRKÇE';
        if (normalized === 'sosyal bilimler') return 'SOSYAL BİL.';
        if (normalized === 'temel matematik') return 'TEMEL MAT.';
        if (normalized === 'fen bilimleri') return 'FEN BİL.';
        if (normalized.includes('turk dili ve edebiyati') || normalized.includes('edebiyati sosyal bilimler 1')) {
            return 'TÜRK D. VE EDEB.';
        }
        if (normalized === 'sosyal bilimler 2') return 'SOSYAL BİL.-2';
        if (normalized === 'matematik') return 'MATEMATİK';

        return (name || 'TEST').toUpperCase().substring(0, 16);
    };

    const filteredQuestions = filterAlternativeBranchQuestions(allQuestions);
    const filteredQuestionMap = filteredQuestions.reduce((acc: Record<string, any[]>, question: any) => {
        const key = String(question?.testId || '');
        if (!acc[key]) acc[key] = [];
        acc[key].push(question);
        return acc;
    }, {});

    type ReportColumn = {
        header: string;
        fullHeader: string;
        testId: string | number;
        data: {
            totalQ: number;
            correct: number;
            incorrect: number;
            empty: number;
            net: number;
            avgNet: number;
            avgSuccessRate: number;
        };
        questions: any[];
        originalIndex: number;
    };

    const columns: ReportColumn[] = subjects.map((s: any, index: number) => {
        const scopedQuestions = [...(filteredQuestionMap[String(s.testId)] || s.questions || [])].sort((left: any, right: any) => {
            const leftOrder = Number(left?.questionNumber || 0);
            const rightOrder = Number(right?.questionNumber || 0);
            return leftOrder - rightOrder;
        });
        const totalQ = scopedQuestions.length || s.totalQ || 0;
        const correct = scopedQuestions.filter((question: any) => question.isCorrect).length;
        const incorrect = scopedQuestions.filter((question: any) => question.isAnswered && !question.isCorrect).length;
        const empty = scopedQuestions.filter((question: any) => !question.isAnswered).length;
        const net = correct - (incorrect * 0.25);

        return {
            header: getCompactTestLabel(s.name || ''),
            fullHeader: s.name || `Test ${index + 1}`,
            testId: s.testId,
            data: {
                totalQ,
                correct,
                incorrect,
                empty,
                net,
                avgNet: s.avgNet || 0,
                avgSuccessRate: s.avgSuccessRate || 0
            },
            questions: scopedQuestions,
            originalIndex: index,
        };
    }).sort((left: ReportColumn, right: ReportColumn) => {
        return getColumnRank(left.fullHeader, left.data.totalQ, left.originalIndex) - getColumnRank(right.fullHeader, right.data.totalQ, right.originalIndex);
    });

    const scoring = stats?.scoring || {};
    const scoreTypes = scoring?.scoreTypes || {};
    const primaryScore = scoring?.primaryScore || null;
    const sessionScores = scoring?.sessionScores || {};
    const sessionRankings = scoring?.sessionRankings || {};
    const ogmResponseScores = scoring?.externalScoring?.response?.scores || {};
    const getScoreTypeValue = (scoreType: string) => {
        const profile = scoreTypes?.[scoreType];
        return Number((profile?.score ?? profile?.rawScore) || 0).toFixed(3);
    };
    const yksAytScoreColumns = [
        { key: 'SAY', label: 'Sayısal', score: getScoreTypeValue('SAY') },
        { key: 'EA', label: 'Eşit Ağırlık', score: getScoreTypeValue('EA') },
        { key: 'SOZ', label: 'Sözel', score: getScoreTypeValue('SOZ') },
    ].filter((item) => scoreTypes?.[item.key]);
    const normalizedHeaders = columns.map((column: ReportColumn) => normalizeExamLabel(column.fullHeader));
    const ykstSessionLabels = [
        sessionScores?.labels?.primary,
        sessionScores?.labels?.secondary,
        sessionScores?.labels?.total,
    ]
        .filter(Boolean)
        .map((label: string) => normalizeExamLabel(label));
    const normalizedYksTytHeaders = ['turkce', 'sosyal bilimler', 'temel matematik', 'fen bilimleri'];
    const hasOnlyYksTytHeaders = normalizedHeaders.length > 0
        && normalizedHeaders.every((header: string) => normalizedYksTytHeaders.includes(header));
    const hasYksAytHeaders = normalizedHeaders.some((header: string) => header.includes('turk dili ve edebiyati') || header.includes('edebiyati sosyal bilimler 1'));
    const isLikelyYKS = examConfig?.levelType === 'yks'
        || examConfig?.yksMode === 'single_tyt'
        || ykstSessionLabels.includes('tyt')
        || (normalizedHeaders.includes('turkce')
            && normalizedHeaders.includes('temel matematik')
            && (hasYksAytHeaders || hasOnlyYksTytHeaders));

    const groupDefinitions = isLikelyYKS
        ? [Math.min(4, columns.length), Math.max(columns.length - 4, 0)].filter((size) => size > 0)
        : [4, 2, 3, 3, 7, 1];

    interface SessionGroup {
        id: number;
        colSpan: number;
        startIndex: number;
        columns: any[];
    }

    const sessionGroups: SessionGroup[] = [];
    let currentIndex = 0;

    groupDefinitions.forEach((size, idx) => {
        if (currentIndex < columns.length) {
            const count = Math.min(size, columns.length - currentIndex);
            if (count > 0) {
                sessionGroups.push({
                    id: idx + 1,
                    colSpan: count,
                    startIndex: currentIndex,
                    columns: columns.slice(currentIndex, currentIndex + count)
                });
                currentIndex += count;
            }
        }
    });

    while (currentIndex < columns.length) {
        sessionGroups.push({
            id: sessionGroups.length + 1,
            colSpan: 1,
            startIndex: currentIndex,
            columns: [columns[currentIndex]]
        });
        currentIndex++;
    }

    const rowLabels = [
        { label: 'SORU SAYISI', key: 'totalQ', fmt: (v: any) => v },
        { label: 'DOĞRU CEVAP', key: 'correct', fmt: (v: any) => v },
        { label: 'YANLIŞ CEVAP', key: 'incorrect', fmt: (v: any) => v },
        { label: 'BOŞ CEVAP', key: 'empty', fmt: (v: any) => v },
        { label: 'NET', key: 'net', fmt: (v: any) => Number(v).toFixed(2) },
        { label: 'BAŞARI ORANI', key: 'success', fmt: (v: any) => `%${v}` },
        { label: 'SINAV NET ORT.', key: 'avg', fmt: (v: any) => Number(v).toFixed(2) },
    ];

    const getColData = (col: any, key: string) => {
        if (key === 'success') return col.data.totalQ > 0 ? ((col.data.net / col.data.totalQ) * 100).toFixed(0) : '0';
        if (key === 'avg') return Number(col.data.avgNet || 0).toFixed(2);

        return col.data[key];
    };

    const yksSessionColumns = isLikelyYKS ? [
        { key: 'TYT', group: 'TYT', label: sessionScores?.labels?.primary || 'TYT', score: Number((sessionScores?.tytScore ?? sessionScores?.session1Score) || 0).toFixed(3) },
        ...(yksAytScoreColumns.length > 0
            ? yksAytScoreColumns.map((item) => ({ ...item, group: 'AYT' }))
            : sessionScores?.labels?.secondary
                ? [{ key: 'AYT', group: 'AYT', label: sessionScores.labels.secondary, score: Number((sessionScores?.aytScore ?? sessionScores?.session2Score) || 0).toFixed(3) }]
                : []),
    ] : [];
    const nonScoreSessionLabels = isLikelyYKS
        ? [
            sessionScores?.labels?.primary || 'TYT',
            ...(yksAytScoreColumns.length > 0 || sessionScores?.labels?.secondary ? [sessionScores?.labels?.secondary || 'AYT'] : []),
        ]
        : ['1. Oturum', '2. Oturum'];
    const scoreGridColumns = sessionGroups;

    const formatRank = (value: any) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) && numeric > 0 ? numeric.toLocaleString('tr-TR') : '-';
    };

    const getYksRankValue = (column: any, scope: 'turkiye' | 'kurum' | 'sube') => {
        const scoreType = column?.key;
        if (scope === 'turkiye') {
            const ogmRank = ogmResponseScores?.[scoreType]?.averageRank;
            if (ogmRank) return formatRank(ogmRank);
            return formatRank(scoreTypes?.[scoreType]?.rankings?.estimatedNational?.estimatedRank);
        }

        if (scoreType === 'TYT') {
            const ranking = scope === 'kurum'
                ? sessionRankings?.tyt?.general
                : sessionRankings?.tyt?.school;
            return formatRank(ranking?.rank);
        }

        const ranking = scope === 'kurum'
            ? scoreTypes?.[scoreType]?.rankings?.general
            : scoreTypes?.[scoreType]?.rankings?.school;
        return formatRank(ranking?.rank);
    };

    const getScoreColor = (score: number | string, sessionCount: number = 1) => {
        const numScore = typeof score === 'string' ? parseFloat(score) : score;
        const redThreshold = 300 / sessionCount;
        const orangeThreshold = 400 / sessionCount;

        if (numScore < redThreshold) return 'text-red-600';
        if (numScore < orangeThreshold) return 'text-orange-500';
        return 'text-green-600';
    };

    const getQuestionsForSubject = (subjectName: string) => {
        return filteredQuestions.filter((q: any) =>
            (q.testName || q.subjectName || '').toLowerCase().includes(subjectName.toLowerCase())
        );
    };

    const buildAnswerStrip = (questions: any[]) => ({
        correctAnswers: (questions || []).map(q => q.correctAnswer || ''),
        studentAnswers: (questions || []).map(q => q.studentAnswer || '')
    });

    const answerStrips = columns.slice(0, 8).map((col: any) => ({
        name: col.header,
        ...buildAnswerStrip(col.questions || [])
    }));

    const filteredAchievements = (() => {
        const rebuilt: any[] = [];
        const sourceQuestions = filteredQuestions.filter((question: any) => (
            question.achievement
            && question.isAnswered
            && !question.isCorrect
        ));

        sourceQuestions.forEach((question: any) => {
            const subject = question.subjectName || question.testName || 'Genel';
            const existing = rebuilt.find((item) => item.subject === subject && item.name === question.achievement);
            if (existing) {
                existing.total += 1;
                existing.incorrect += 1;
                return;
            }

            rebuilt.push({
                subject,
                name: question.achievement,
                total: 1,
                correct: 0,
                incorrect: 1,
            });
        });

        return rebuilt.sort((left: any, right: any) => {
            const subjectCompare = (left.subject || '').localeCompare((right.subject || ''), 'tr');
            if (subjectCompare !== 0) return subjectCompare;
            return (left.name || '').localeCompare((right.name || ''), 'tr');
        });
    })();

    let allTopics = filteredAchievements.length > 0
        ? filteredAchievements.map((a: any) => ({
            subject: a.subject?.toUpperCase() || 'GENEL',
            name: a.name || 'Kazanım',
            total: a.total || 0,
            correct: a.correct || 0,
            incorrect: a.incorrect || 0,
            percent: a.total > 0 ? Math.round((a.correct / a.total) * 100) : 0
        }))
        :
        subjects.flatMap((s: any) =>
            (s.subjects || []).slice(0, 10).map((sub: any) => ({
                subject: s.name?.toUpperCase() || 'TEST',
                name: sub.name || 'Alt Ders',
                total: sub.totalQuestions || 0,
                correct: sub.correct || 0,
                incorrect: sub.incorrect || 0,
                percent: sub.percentage || 0
            }))
        );

    const topicColumnSize = Math.max(1, Math.ceil(allTopics.length / 3));
    const topicCols = [
        allTopics.slice(0, topicColumnSize),
        allTopics.slice(topicColumnSize, topicColumnSize * 2),
        allTopics.slice(topicColumnSize * 2)
    ].map((topics) => topics.length > 0 ? topics : [{ subject: '', name: '', total: 0, correct: 0, incorrect: 0, percent: 0 }]);

    return (
        <div className="flex flex-col w-full h-full bg-white text-[10px] font-sans leading-tight p-2" style={{ fontFamily: 'Arial Narrow, Arial, sans-serif', minHeight: '287mm' }}>

            <div className="flex flex-col">
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1">

                        <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
                            {companyLogo ? (
                                <img src={companyLogo} alt={companyName || 'Logo'} className="w-full h-full object-contain" />
                            ) : (
                                <span className="text-[9px] text-gray-400">LOGO</span>
                            )}
                        </div>
                        <div className="font-bold text-[10px] text-gray-800 whitespace-nowrap">{companyName || 'İSTANBUL / ESENLER / SAYE EĞİTİM'}</div>
                    </div>
                    <div>
                        <div className="font-bold text-[12px] text-blue-600 uppercase tracking-tight">SINAV SONUÇ BELGESİ</div>
                    </div>
                </div>
                <div className="text-center w-full">
                    <div className="text-[8px] italic text-gray-500 mb-1.5">
                        Yanlış yaptığınız soruları görebilmek ve çözüm videolarını izleyebilmek için "Akbim ODS" uygulamasını indiriniz.
                    </div>
                </div>
            </div>

            <div className="flex border border-black text-center  text-[9px]">

                <div className="flex w-[30%]">
                    <div className="w-1/4 border-r border-black">
                        <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">KİTAPÇIK</div>
                        <div className="bg-[#F2F2F2] py-0.5 font-bold">{bookletType || 'A'}</div>
                    </div>
                    <div className="w-1/4 border-r border-black">
                        <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">ŞUBE</div>
                        <div className="bg-[#F2F2F2] py-0.5 font-bold">{sube}</div>
                    </div>
                    <div className="w-1/4 border-r border-black">
                        <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">ÖĞR.NO</div>
                        <div className="bg-[#F2F2F2] py-0.5 font-bold">{studentNo}</div>
                    </div>
                    <div className="w-1/4 border-r border-black">
                        <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">TC NO</div>
                        <div className="bg-[#F2F2F2] py-0.5 font-bold">{tcNo}</div>
                    </div>
                </div>

                <div className="w-[20%] border-r border-black overflow-hidden">
                    <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">ADI - SOYADI</div>
                    <div className="bg-[#F2F2F2] py-0.5 font-bold uppercase whitespace-nowrap truncate px-1">{fullName}</div>
                </div>

                <div className="flex-1 border-r border-black overflow-hidden">
                    <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">SINAV ADI</div>
                    <div className="bg-[#F2F2F2] py-0.5 font-bold uppercase whitespace-nowrap truncate px-1">{examTitle}</div>
                </div>

                <div className="w-20">
                    <div className="bg-[#F2F2F2] font-bold py-0.5 border-b border-black whitespace-nowrap">TARİH</div>
                    <div className="bg-[#F2F2F2] py-0.5 font-bold">{new Date().toLocaleDateString('tr-TR')}</div>
                </div>
            </div>

            <div className="overflow-hidden">
                <table className="w-full text-center border-collapse text-[8px] border border-black">
                    <thead>
                        <tr>
                            <th rowSpan={2} className="bg-[#F8CBAD] border border-black font-bold whitespace-nowrap" style={{ width: '4rem' }}>OTURUMLAR</th>

                            {sessionGroups.map((group, idx) => (
                                <th
                                    key={group.id}
                                    colSpan={group.colSpan}
                                    className={`bg-[#F8CBAD] border-t border-b border-black py-0.5 whitespace-nowrap ${idx === sessionGroups.length - 1 ? 'border-r border-black' : 'border-r-2 border-black'}`}
                                >
                                    {isLikelyYKS
                                        ? (group.id === 1 ? 'TYT' : group.id === 2 ? 'AYT' : `${group.id}. Bölüm`)
                                        : `${group.id}. Oturum`}
                                </th>
                            ))}
                        </tr>
                        <tr>
                            {columns.map((col: any, i: number) => {
                                const isGroupEnd = sessionGroups.some(g => g.startIndex + g.colSpan - 1 === i);
                                const isLastCol = i === columns.length - 1;
                                return (
                                    <th key={i} className={`bg-gray-200 border-b border-black py-0.5 px-0.5 font-bold whitespace-nowrap ${isLastCol ? 'border-r border-r-black' : isGroupEnd ? 'border-r-2 border-r-black' : 'border-r border-r-gray-400'}`}>{col.header}</th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {rowLabels.map((row, rIdx) => (
                            <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="bg-[#F8CBAD] border-l border-r border-r-black border-b border-gray-400 font-bold text-left px-1 py-0.5 whitespace-nowrap">{row.label}</td>
                                {columns.map((col: any, cIdx: number) => {
                                    const isGroupEnd = sessionGroups.some(g => g.startIndex + g.colSpan - 1 === cIdx);
                                    const isLastCol = cIdx === columns.length - 1;
                                    return (
                                        <td key={cIdx} className={`border-b border-gray-300 py-0.5 whitespace-nowrap ${isLastCol ? 'border-r border-r-black' : isGroupEnd ? 'border-r-2 border-r-black' : 'border-r border-r-gray-300'}`}>{getColData(col, row.key)}</td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <table className="w-full border border-black text-[8px] border-collapse">
                <thead>

                    <tr className="border-b border-black">
                        <th colSpan={2} rowSpan={2} className="bg-[#F2F2F2] font-bold px-1 py-0.5 border-l border-r border-black text-left">PUANLAR</th>
                        {isLikelyYKS ? (
                            <>
                                {yksAytScoreColumns.length > 0 ? (
                                    <>
                                        <th className="bg-[#F8CBAD] font-bold px-1 py-0.5 text-center w-12 border-r border-gray-300">TYT</th>
                                        <th colSpan={yksAytScoreColumns.length} className="bg-[#F8CBAD] font-bold px-1 py-0.5 text-center border-r-2 border-black">AYT</th>
                                    </>
                                ) : (
                                    yksSessionColumns.map((item, idx) => (
                                        <th key={item.key} className={`bg-[#F8CBAD] font-bold px-1 py-0.5 text-center w-12 ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{item.label}</th>
                                    ))
                                )}
                                <th colSpan={nonScoreSessionLabels.length} className="bg-[#FFF2CC] font-bold px-1 py-0.5 border-r-2 border-black text-center">TESTE GÖRE ÖĞRENCİ SAYILARI</th>
                                <th className="bg-[#FFCCCC] font-bold px-1 py-0.5 text-center whitespace-nowrap" colSpan={nonScoreSessionLabels.length}>Kitapçık Türü</th>
                            </>
                        ) : (
                            <>
                                <th className="bg-[#F8CBAD] font-bold px-1 py-0.5 border-r border-gray-300 text-center w-12">Genel</th>
                                {sessionGroups.map((group, idx) => (
                                    <th key={group.id} className={`bg-[#F8CBAD] font-bold px-1 py-0.5 text-center w-10 ${idx === sessionGroups.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{group.id}. Oturum</th>
                                ))}
                                <th colSpan={sessionGroups.length} className="bg-[#FFF2CC] font-bold px-1 py-0.5 border-r-2 border-black text-center">TESTE GÖRE ÖĞRENCİ SAYILARI</th>
                                <th className="bg-[#FFCCCC] font-bold px-1 py-0.5 text-center whitespace-nowrap" colSpan={sessionGroups.length}>Kitapçık Türü</th>
                            </>
                        )}
                    </tr>

                    <tr style={{ height: "10px" }} className="border-b border-gray-300">
                        {isLikelyYKS ? (
                            <>
                                {yksSessionColumns.map((item, idx) => (
                                    <td key={item.key} className={`px-1 py-0.5 text-center font-bold ${getScoreColor(item.score)} ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>
                                        {item.group === 'AYT' && yksAytScoreColumns.length > 0 ? (
                                            <div className="leading-tight">
                                                <div className="text-[7px] text-gray-700">{item.label}</div>
                                                <div>{item.score}</div>
                                            </div>
                                        ) : item.score}
                                    </td>
                                ))}
                                {nonScoreSessionLabels.map((item, idx) => (
                                    <td key={`c-${item}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{item}</td>
                                ))}
                                {nonScoreSessionLabels.map((item, idx) => (
                                    <td key={`b-${item}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r border-black' : 'border-r border-gray-300'}`}>{item}</td>
                                ))}
                            </>
                        ) : (
                            <>
                                <td className={`px-1 py-0.5 border-r border-gray-300 text-center font-bold ${getScoreColor(Number(primaryScore?.score || 0))}`}>{Number(primaryScore?.score || 0).toFixed(3)}</td>
                                {sessionGroups.map((group, idx) => (
                                    <td key={group.id} className={`px-1 py-0.5 text-center font-bold ${idx === sessionGroups.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>0.000</td>
                                ))}
                                {sessionGroups.map((group, idx) => (
                                    <td key={group.id} className={`px-1 py-0.5 text-center font-bold ${idx === sessionGroups.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{group.id}. Oturum</td>
                                ))}
                                {sessionGroups.map((group, idx) => (
                                    <td key={`b-${group.id}`} className={`px-1 py-0.5 text-center font-bold ${idx === sessionGroups.length - 1 ? 'border-r border-black' : 'border-r border-gray-300'}`}>{group.id}. Oturum</td>
                                ))}
                            </>
                        )}
                    </tr>
                </thead>
                <tbody>

                    <tr className="border-b border-gray-200">
                        <td rowSpan={5} className="bg-gray-200 font-bold border-l border-r border-t border-black p-0 w-4 text-[9px] align-middle">
                            <div className="flex items-center justify-center w-full h-full" style={{ padding: '4px 0' }}>
                                <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>DERECELER</div>
                            </div>
                        </td>
                        <td className="bg-[#F8CBAD] font-bold px-1 py-0.5 border-r border-t border-black whitespace-nowrap">TÜRKİYE</td>
                        {isLikelyYKS ? (
                            <>
                                {yksSessionColumns.map((g: any, idx: number) => (
                                    <td key={`tr-${idx}`} className={`px-1 py-0.5 text-center ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{getYksRankValue(g, 'turkiye')}</td>
                                ))}
                                {nonScoreSessionLabels.map((g: any, idx: number) => (
                                    <td key={`trc-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                                {nonScoreSessionLabels.map((g: any, idx: number) => (
                                    <td key={`trb-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r border-black' : 'border-r border-gray-300'}`}>{bookletType || 'A'}</td>
                                ))}
                            </>
                        ) : (
                            <>
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`tr-${idx}`} className={`px-1 py-0.5 text-center ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`trc-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`trb-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === scoreGridColumns.length - 1 ? 'border-r border-black' : 'border-r border-gray-300'}`}>{bookletType || 'A'}</td>
                                ))}
                            </>
                        )}
                    </tr>

                    <tr className="border-b border-gray-200">
                        <td className="bg-[#F8CBAD] font-bold px-1 py-0.5 border-r border-black whitespace-nowrap">İL</td>
                        {isLikelyYKS ? (
                            <>
                                {yksSessionColumns.map((g: any, idx: number) => (
                                    <td key={`il-${idx}`} className={`px-1 py-0.5 text-center ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {nonScoreSessionLabels.map((g: any, idx: number) => (
                                    <td key={`ilc-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                                <td colSpan={nonScoreSessionLabels.length} rowSpan={4} className="text-center align-middle border-l border-gray-300">
                                    <div className="font-bold text-[9px]">SINAVA GİREN ÖĞRENCİ</div>
                                    <div className="font-bold text-[9px]">SAYISI</div>
                                    <div className="text-sm font-bold mt-0.5">{totalStudents}</div>
                                </td>
                            </>
                        ) : (
                            <>
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`il-${idx}`} className={`px-1 py-0.5 text-center ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`ilc-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                                <td colSpan={scoreGridColumns.length} rowSpan={4} className="text-center align-middle border-l border-gray-300">
                                    <div className="font-bold text-[9px]">SINAVA GİREN ÖĞRENCİ</div>
                                    <div className="font-bold text-[9px]">SAYISI</div>
                                    <div className="text-sm font-bold mt-0.5">{totalStudents}</div>
                                </td>
                            </>
                        )}
                    </tr>

                    <tr className="border-b border-gray-200">
                        <td className="bg-[#F8CBAD] font-bold px-1 py-0.5 border-r border-black whitespace-nowrap">İLÇE</td>
                        {isLikelyYKS ? (
                            <>
                                {yksSessionColumns.map((g: any, idx: number) => (
                                    <td key={`ilce-${idx}`} className={`px-1 py-0.5 text-center ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {nonScoreSessionLabels.map((g: any, idx: number) => (
                                    <td key={`ilcec-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                            </>
                        ) : (
                            <>
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`ilce-${idx}`} className={`px-1 py-0.5 text-center ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`ilcec-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                            </>
                        )}
                    </tr>

                    <tr className="border-b border-gray-200">
                        <td className="bg-[#F8CBAD] font-bold px-1 py-0.5 border-r border-black whitespace-nowrap">KURUM</td>
                        {isLikelyYKS ? (
                            <>
                                {yksSessionColumns.map((g: any, idx: number) => (
                                    <td key={`kurum-${idx}`} className={`px-1 py-0.5 text-center ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{getYksRankValue(g, 'kurum')}</td>
                                ))}
                                {nonScoreSessionLabels.map((g: any, idx: number) => (
                                    <td key={`kurumc-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                            </>
                        ) : (
                            <>
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`kurum-${idx}`} className={`px-1 py-0.5 text-center ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`kurumc-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                            </>
                        )}
                    </tr>

                    <tr>
                        <td className="bg-[#F8CBAD] font-bold px-1 py-0.5 border-r border-black whitespace-nowrap">ŞUBE</td>
                        {isLikelyYKS ? (
                            <>
                                {yksSessionColumns.map((g: any, idx: number) => (
                                    <td key={`sube-${idx}`} className={`px-1 py-0.5 text-center ${idx === yksSessionColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{getYksRankValue(g, 'sube')}</td>
                                ))}
                                {nonScoreSessionLabels.map((g: any, idx: number) => (
                                    <td key={`subec-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === nonScoreSessionLabels.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                            </>
                        ) : (
                            <>
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`sube-${idx}`} className={`px-1 py-0.5 text-center ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>-</td>
                                ))}
                                {scoreGridColumns.map((g: any, idx: number) => (
                                    <td key={`subec-${idx}`} className={`px-1 py-0.5 text-center font-bold ${idx === scoreGridColumns.length - 1 ? 'border-r-2 border-black' : 'border-r border-gray-300'}`}>{totalStudents}</td>
                                ))}
                            </>
                        )}
                    </tr>
                </tbody>
            </table>

            <div className="flex items-start">

                <div className="w-1/2 border-r border-black">
                    <table className="w-full border border-black text-[7px] border-collapse">
                        <tbody>
                            {columns.slice(0, Math.ceil(columns.length / 2)).map((col: any, idx: number) => (
                                <React.Fragment key={idx}>
                                    <tr style={{ height: "10px" }} className="border-b border-gray-300">
                                        <td className="bg-[#F2F2F2] font-bold px-1 py-0.5 border-l border-r border-black w-20 whitespace-nowrap text-[6px]">{col.header}</td>
                                        <td className="px-0.5 py-0.5">
                                            <div className="flex">
                                                {(col.questions || []).map((q: any, i: number) => (
                                                    <span key={i} style={{ width: "6px", minWidth: "6px" }} className="text-center font-bold">{q.correctAnswer || ''}</span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr style={{ height: "10px" }} className={`border-b border-gray-300 bg-[#FFFFCC]`}>
                                        <td className="font-bold px-1 py-0.5 border-l border-r border-black whitespace-nowrap text-[6px]">ÖĞRENCİ CEVABI</td>
                                        <td className="px-0.5 py-0.5">
                                            <div className="flex">
                                                {(col.questions || []).map((q: any, i: number) => (
                                                    <span
                                                        key={i}
                                                        style={{ width: "6px", minWidth: "6px" }}
                                                        className={`text-center ${q.isCorrect ? 'text-green-700 font-bold' : q.isAnswered ? 'text-red-600' : ''}`}
                                                    >
                                                        {q.studentAnswer || ''}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="w-1/2">
                    <table className="w-full border border-black text-[7px] border-collapse">
                        <tbody>
                            {columns.slice(Math.ceil(columns.length / 2)).map((col: any, idx: number) => (
                                <React.Fragment key={idx}>
                                    <tr style={{ height: "10px" }} className="border-b border-gray-300">
                                        <td className="bg-[#F2F2F2] font-bold px-1 py-0.5 border-l border-r border-black w-20 whitespace-nowrap text-[6px]">{col.header}</td>
                                        <td className="px-0.5 py-0.5">
                                            <div className="flex">
                                                {(col.questions || []).map((q: any, i: number) => (
                                                    <span key={i} style={{ width: "6px", minWidth: "6px" }} className="text-center font-bold">{q.correctAnswer || ''}</span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr style={{ height: "10px" }} className={`border-b border-gray-300 bg-[#FFFFCC]`}>
                                        <td className="font-bold px-1 py-0.5 border-l border-r border-black whitespace-nowrap text-[6px]">ÖĞRENCİ CEVABI</td>
                                        <td className="px-0.5 py-0.5">
                                            <div className="flex">
                                                {(col.questions || []).map((q: any, i: number) => (
                                                    <span
                                                        key={i}
                                                        style={{ width: "6px", minWidth: "6px" }}
                                                        className={`text-center ${q.isCorrect ? 'text-green-700 font-bold' : q.isAnswered ? 'text-red-600' : ''}`}
                                                    >
                                                        {q.studentAnswer || ''}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex-1 flex">
                {topicCols.map((topics, colIdx) => (
                    <div key={colIdx} className={`w-1/3 flex flex-col ${colIdx < 2 ? 'border-r border-black' : ''}`}>
                        <table className="w-full text-left border-collapse text-[7px] border border-black">
                            <thead>
                                <tr className="bg-[#F8CBAD]">
                                    <th className="pl-1 text-center border-l border-b border-r border-black font-bold py-0.5">KONU ADI</th>
                                    <th className="w-4 text-center border-b border-r border-black font-bold py-0.5">SS</th>
                                    <th className="w-4 text-center border-b border-r border-black font-bold py-0.5">D</th>
                                    <th className="w-4 text-center border-b border-r border-black font-bold py-0.5">Y</th>
                                    <th className="w-4 text-center border-b border-r border-black font-bold py-0.5">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topics.map((t: any, idx: number) => (
                                    <React.Fragment key={idx}>
                                        {(idx === 0 || topics[idx - 1].subject !== t.subject) && (
                                            <tr className="bg-[#FFF2CC] font-bold border-t border-black">
                                                <td colSpan={5} className="pl-1 border-b border-black py-0.5">{t.subject}</td>
                                            </tr>
                                        )}
                                        <tr className="bg-white border-b border-gray-300">
                                            <td className="pl-1 border-l border-r border-gray-300 truncate max-w-[100px] py-0.5" title={t.name}>{t.name}</td>
                                            <td className="text-center border-r border-gray-300 font-bold py-0.5">{t.total}</td>
                                            <td className="text-center border-r border-gray-300 font-bold py-0.5">{t.correct}</td>
                                            <td className="text-center border-r border-gray-300 font-bold py-0.5">{t.incorrect}</td>
                                            <td className="text-center border-r font-bold py-0.5">{t.percent}</td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        </div>
    );
};
